/**
 * Simple Wizard Store
 * 
 * Clean, single-source-of-truth navigation and state management.
 * Replaces the over-engineered multi-layer navigation system.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { WizardData, WizardConfig } from '../types/WizardTypes';

export interface SimpleWizardState<TData extends WizardData> {
  // Core data
  data: TData;
  
  // Navigation (single source of truth)
  currentStep: number;           // 1-based step number
  validatedSteps: string[];      // Steps user can navigate to
  
  // UI state
  isLoading: boolean;
  isSubmitting: boolean;
  isDirty: boolean;
  
  // Configuration
  config: WizardConfig<TData> | null;
}

export interface SimpleWizardActions<TData extends WizardData> {
  // Initialization
  initialize: (config: WizardConfig<TData>, initialData: TData) => void;
  
  // Data management
  updateData: (updates: Partial<TData>) => void;
  
  // Navigation
  goToStep: (stepNumber: number) => void;
  canNavigateToStep: (stepNumber: number) => boolean;
  
  // Step validation
  validateStep: (stepId: string) => void;
  invalidateStepsAfter: (stepId: string) => void;
  
  // Persistence
  markDirty: () => void;
  markClean: () => void;
}

export type SimpleWizardStore<TData extends WizardData> = SimpleWizardState<TData> & SimpleWizardActions<TData>;

export function createSimpleWizardStore<TData extends WizardData>() {
  return create<SimpleWizardStore<TData>>()(
    devtools((set, get) => ({
      // Initial state
      data: {} as TData,
      currentStep: 1,
      validatedSteps: [],
      isLoading: false,
      isSubmitting: false,
      isDirty: false,
      config: null,
      
      // Initialize wizard
      initialize: (config: WizardConfig<TData>, initialData: TData) => {
        const savedStep = (initialData as Record<string, unknown>).currentWizardStep as number || 1;
        const validatedSteps = (initialData as Record<string, unknown>).validatedSteps as string[] || [config.steps[0]?.id];
        
        
        set({
          config,
          data: initialData,
          currentStep: savedStep,
          validatedSteps,
          isLoading: false,
        });
      },
      
      // Update data and track changes
      updateData: (updates: Partial<TData>) => {
        const currentData = get().data;
        const currentStepNumber = get().currentStep;
        const config = get().config;
        
        if (!config) return;
        
        // Check for calculation field changes (exclude navigation-only changes)
        const calculationFields = [
          'policyType', 'quote'
        ];
        
        // Note: Change detection working correctly
        
        // Only check for actual data changes, not navigation changes
        const hasCalculationChanges = calculationFields.some(field => {
          if (!Object.prototype.hasOwnProperty.call(updates, field)) {
            return false;
          }
          
          if (field === 'quote') {
            // For quote changes, only trigger recalc for fields that actually affect calculation
            const quoteUpdates = updates[field as keyof TData] as Record<string, unknown>;
            const currentQuote = (currentData[field as keyof TData] as Record<string, unknown>) || {};
            
            // Fields that should trigger recalculation
            const calculationAffectingFields = [
              'coverageStartDate', 'coverageEndDate', 'strikeLevel', 'isMasterPolicy',
              'underlying_assets', 'perils_config', 'participants', 'product', 'productId'
            ];
            
            // Check if any calculation-affecting field has actually changed
            return calculationAffectingFields.some(fieldName => {
              const newValue = quoteUpdates[fieldName];
              const currentValue = currentQuote[fieldName];
              return newValue !== undefined && newValue !== currentValue;
            });
          }
          
          return updates[field as keyof TData] !== currentData[field as keyof TData];
        });
        
        
        // CRITICAL: Don't invalidate steps for navigation-only changes
        const isNavigationOnly = Object.keys(updates).every(key => 
          key === 'currentWizardStep' || key === 'quoteId'
        );
        
        
        // Simple invalidation: calculation changes invalidate subsequent steps
        let newValidatedSteps = get().validatedSteps;
        
        
        if (hasCalculationChanges && !isNavigationOnly) {
          const currentStepId = config.steps[currentStepNumber - 1]?.id;
          const currentStepIndex = config.steps.findIndex(s => s.id === currentStepId);
          newValidatedSteps = config.steps.slice(0, currentStepIndex + 1).map(s => s.id);
          
          // Set recalc needed if quotation results exist (check both locations)
          const hasExistingResults = (currentData as Record<string, unknown>).quotationResults || 
                                   ((currentData as Record<string, unknown>).quote as Record<string, unknown>)?.quotationResults;
          const updatingResults = (updates as Record<string, unknown>).quotationResults;
          const updatingQuoteResults = ((updates as Record<string, unknown>).quote as Record<string, unknown>)?.quotationResults;
          
          // Check if we're actually providing NEW results (not just copying existing ones)
          const currentQuoteResults = ((currentData as Record<string, unknown>).quote as Record<string, unknown>)?.quotationResults;
          const isActuallyProvidingNewResults = (updatingResults && updatingResults !== (currentData as Record<string, unknown>).quotationResults) ||
                                               (updatingQuoteResults && updatingQuoteResults !== currentQuoteResults);
          
          console.log('🔍 RECALC DETECTION - RESULTS CHECK:', {
            hasExistingResults,
            updatingResults: !!updatingResults,
            updatingQuoteResults: !!updatingQuoteResults,
            isActuallyProvidingNewResults,
            currentRecalcNeeded: (currentData as Record<string, unknown>).recalcNeeded,
            resultsSame: updatingQuoteResults === currentQuoteResults,
            timestamp: new Date().toISOString()
          });
          
          // Only set recalc needed if we have existing results AND we're not providing new results
          if (hasExistingResults && !isActuallyProvidingNewResults) {
            (updates as Record<string, unknown>).recalcNeeded = true;
            console.log('🔍 RECALC DETECTION - SETTING RECALC NEEDED = TRUE');
          } else {
            console.log('🔍 RECALC DETECTION - NO RECALC NEEDED:', {
              reason: hasExistingResults ? 'providing new results' : 'no existing results',
              isActuallyProvidingNewResults
            });
          }
        }
        
        const newData = { 
          ...currentData, 
          ...updates,
          currentWizardStep: currentStepNumber // Always keep current step in sync
        };
        
        
        
        set({
          data: newData,
          validatedSteps: newValidatedSteps,
          isDirty: true,
        });
      },
      
      // Navigate to step
      goToStep: (stepNumber: number) => {
        const config = get().config;
        if (!config || stepNumber < 1 || stepNumber > config.steps.length) {
          return;
        }
        
        set(state => ({
          currentStep: stepNumber,
          data: {
            ...state.data,
            currentWizardStep: stepNumber
          }
        }));
        
      },
      
      // Check if can navigate to step
      canNavigateToStep: (stepNumber: number) => {
        const config = get().config;
        const validatedSteps = get().validatedSteps;
        
        if (!config || stepNumber < 1 || stepNumber > config.steps.length) {
          return false;
        }
        
        const stepId = config.steps[stepNumber - 1]?.id;
        const canNavigate = stepId ? validatedSteps.includes(stepId) : false;
        
        
        return canNavigate;
      },
      
      // Validate step (add to validatedSteps)
      validateStep: (stepId: string) => {
        const validatedSteps = get().validatedSteps;
        if (!validatedSteps.includes(stepId)) {
          set({ validatedSteps: [...validatedSteps, stepId] });
        }
      },
      
      // Invalidate steps after given step
      invalidateStepsAfter: (stepId: string) => {
        const config = get().config;
        if (!config) return;
        
        const stepIndex = config.steps.findIndex(s => s.id === stepId);
        const stepsToKeep = config.steps.slice(0, stepIndex + 1).map(s => s.id);
        
        set({ validatedSteps: stepsToKeep });
      },
      
      // Persistence
      markDirty: () => set({ isDirty: true }),
      markClean: () => set({ isDirty: false }),
    }), {
      name: 'simple-wizard-store',
    })
  );
}
